import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, User as UserIcon } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { PageHeader, Field, inputCls } from "@/components/shared";
import { useNavigate } from "react-router-dom";

export default function Profile() {
  const { user, setUser, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", agent_signature: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user && user !== false) {
      setForm({ name: user.name || "", agent_signature: user.agent_signature || "" });
    }
  }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/me", form);
      setUser(data);
      toast.success(t("saved"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleSignOut = async () => {
    await logout();
    navigate("/login");
  };

  if (!user || user === false) return null;

  return (
    <div data-testid="profile-page">
      <PageHeader title={t("profile")} subtitle={t("profileSubtitle")} />

      <div className="max-w-2xl bg-white border border-stone-200 rounded-2xl p-8">
        <div className="flex items-center gap-4 mb-8 pb-6 border-b border-stone-100">
          {user.picture ? (
            <img data-testid="profile-avatar" src={user.picture} alt="" className="w-16 h-16 rounded-full border border-stone-200" />
          ) : (
            <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center">
              <UserIcon size={24} strokeWidth={1.5} className="text-stone-400" />
            </div>
          )}
          <div>
            <p data-testid="profile-email" className="text-sm font-medium text-stone-900">{user.email}</p>
            <p className="text-xs text-stone-400 mt-0.5">{t("googleAccount")}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5">
          <Field label={t("displayName")}>
            <input
              data-testid="profile-name-input"
              className={inputCls}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={user.email.split("@")[0]}
            />
          </Field>
          <Field label={t("agentSignature")}>
            <textarea
              data-testid="profile-signature-input"
              rows={4}
              className={inputCls}
              value={form.agent_signature}
              onChange={(e) => setForm({ ...form, agent_signature: e.target.value })}
              placeholder={t("agentSignaturePlaceholder")}
            />
            <p className="text-[11px] text-stone-400 mt-1">
              {t("agentSignatureHint")}
            </p>
          </Field>
        </div>

        <div className="flex items-center justify-between mt-8 pt-6 border-t border-stone-100">
          <button
            data-testid="profile-signout-button"
            onClick={handleSignOut}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-stone-600 hover:text-[#7F1D1D] transition-colors">
            <LogOut size={14} strokeWidth={1.5} /> {t("logout")}
          </button>
          <button
            data-testid="profile-save-button"
            onClick={save}
            disabled={saving}
            className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-6 py-2 text-sm font-medium transition-colors disabled:opacity-60">
            {saving ? "…" : t("save")}
          </button>
        </div>
      </div>
    </div>
  );
}
